import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, LifeBuoy, RefreshCw, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const GRADE_TONE = {
  "C": "bg-amber-50 text-amber-800 border-amber-200",
  "Risk": "bg-rose-50 text-rose-800 border-rose-200",
};

export default function RecoveryList() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachingId, setCoachingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/scorecards/recovery-list");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sendCoaching = async (m) => {
    if (!window.confirm(`Send a coaching snapshot to ${m.name}?`)) return;
    setCoachingId(m.userId);
    try {
      const res = await apiClient.post(`/scorecards/managers/${m.userId}/coaching-email`, {});
      if (res.data?.dispatched) toast.success(`Coaching sent to ${res.data.email}`);
      else toast.error(res.data?.reason || "Coaching not dispatched");
    } catch (e) {
      const body = e?.response?.data;
      const sc = e?.response?.status;
      if (sc === 503) toast.error(body?.reason || "Email service not configured");
      else if (sc === 502) toast.error(`Delivery failed: ${body?.reason || "provider error"}`);
      else toast.error(body?.detail || body?.reason || "Failed to send coaching");
    } finally {
      setCoachingId(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 border-slate-200" data-testid="recovery-list-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading recovery list…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden || data.error) return null;

  const rows = data.managers || [];
  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="recovery-list">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-rose-600" />
          <h3 className="font-semibold text-slate-900 text-sm">Recovery List</h3>
          <span className="text-xs text-slate-500">{rows.length} below threshold</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="recovery-list-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="recovery-list-empty">
          No managers below B grade. Strong bench.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="recovery-list-rows">
          {rows.slice(0, 5).map((m) => (
            <div
              key={m.userId}
              className="flex items-start gap-3 p-2.5 rounded-md bg-slate-50 border border-slate-100"
              data-testid={`recovery-row-${m.userId}`}
            >
              <Badge className={`border text-[10px] px-1.5 py-0 mt-0.5 ${GRADE_TONE[m.grade] || GRADE_TONE.C}`}>
                {m.grade}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 font-medium truncate">{m.name}</div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  <span className="font-semibold text-rose-700">{m.mainIssue}</span>
                  <span className="mx-1 text-slate-300">·</span>
                  Upside <span className="font-semibold text-indigo-700">{fmtGBP(m.estUpsideGBP)}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 italic">Fix: {m.recommendedFix}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={coachingId === m.userId}
                onClick={() => sendCoaching(m)}
                className="h-7 text-[11px] text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                data-testid={`recovery-coach-${m.userId}`}
              >
                {coachingId === m.userId ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <MessageSquare className="h-3 w-3 mr-1" />}
                Coach
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
